import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import SynioLogo from './SynioLogo';
import { 
  FaRocket, 
  FaShieldAlt, 
  FaDatabase, 
  FaCog, 
  FaCube, 
  FaImage, 
  FaDice, 
  FaDownload,
  FaCheck,
  FaClock,
  FaCloud,
  FaVideo,
  FaChartLine,
  FaCode,
  FaLock
} from 'react-icons/fa';
import AuthForm from './Auth/AuthForm';
import Impressum from './Impressum';
import './LandingPage.css';

const LandingPage = () => {
  const [showAuth, setShowAuth] = useState(false);
  const [showImpressum, setShowImpressum] = useState(false);
  const { signIn, signUp } = useAuth();

  const currentFeatures = [
    {
      icon: <FaCube />,
      title: '3D Model Support',
      description: 'Unterstützung für GLTF, GLB, FBX und OBJ Formate mit vollständiger Material-Kontrolle'
    },
    {
      icon: <FaDice />,
      title: 'Intelligente Randomisierung',
      description: 'Automatische Variation von Position, Rotation, Beleuchtung und Umgebung für vielfältige Datensätze'
    },
    {
      icon: <FaImage />,
      title: 'YOLO Format Export',
      description: 'Direkter Export im YOLO-Format für Bounding Boxes und Instance Segmentation (Polygon)'
    },
    {
      icon: <FaDatabase />,
      title: 'Lokale Datenverarbeitung',
      description: 'Alle Daten werden lokal in Ihrem Browser generiert - keine Cloud-Uploads erforderlich'
    },
    {
      icon: <FaShieldAlt />,
      title: 'DSGVO-konform',
      description: '100% datenschutzkonform - Ihre Daten verlassen niemals Ihr Gerät'
    },
    {
      icon: <FaCog />,
      title: 'Vollständige Kontrolle',
      description: 'Präzise Einstellungen für Kamera, Materialien, Beleuchtung und Export-Parameter'
    }
  ];

  const comingSoonFeatures = [
    {
      icon: <FaDownload />,
      title: 'Mehr Export-Formate',
      description: 'COCO, Pascal VOC und weitere Annotation-Formate'
    },
    {
      icon: <FaVideo />,
      title: 'Video-Export',
      description: 'Generierung von Video-Sequenzen für Video-basierte ML-Modelle'
    },
    {
      icon: <FaCloud />,
      title: 'Cloud-Speicher Integration',
      description: 'Direkte Integration mit AWS S3, Google Cloud Storage und Azure'
    },
    {
      icon: <FaChartLine />,
      title: 'Erweiterte Augmentation',
      description: 'Helligkeit, Kontrast, Farbverschiebung und weitere Bildtransformationen'
    },
    {
      icon: <FaCode />,
      title: 'API-Zugriff',
      description: 'RESTful API für automatisierte Datensatz-Generierung und Integration in CI/CD'
    },
    {
      icon: <FaDatabase />,
      title: 'Train/Val Split',
      description: 'Automatische Aufteilung in Trainings- und Validierungs-Datensätze'
    }
  ];

  if (showAuth) {
    return <AuthForm />;
  }

  if (showImpressum) {
    return <Impressum onBack={() => setShowImpressum(false)} />;
  }

  return (
    <main className="landing-page" role="main">
      {/* Hero Section */}
      <section className="hero-section-minimal" aria-label="Hero Section">
        <div className="hero-container">
          {/* Abstract 3D Shapes */}
          <div className="hero-shapes">
            <div className="hero-shape shape-1"></div>
            <div className="hero-shape shape-2"></div>
            <div className="hero-shape shape-3"></div>
            <div className="hero-shape shape-4"></div>
            <div className="hero-shape shape-5"></div>
            <div className="hero-shape shape-6"></div>
            <div className="hero-shape shape-7"></div>
            <div className="hero-shape shape-8"></div>
            <div className="hero-shape shape-9"></div>
            <div className="hero-shape shape-10"></div>
            <div className="hero-shape shape-11"></div>
            <div className="hero-shape shape-12"></div>
          </div>
          
          {/* Main Content */}
          <div className="hero-content">
            {/* Logo */}
            <div className="hero-logo" role="img" aria-label="SYNIO Logo">
              <SynioLogo height={40} />
            </div>
            
            <div className="hero-title-wrapper">
              <h1 className="hero-title">
                <span className="hero-title-line-1">
                  <span className="title-word">Synthetic</span>
                  <span className="title-word">Data</span>
                </span>
                <span className="hero-title-line-2">
                  <span className="title-word">Generator</span>
                </span>
              </h1>
              <div className="hero-title-accent"></div>
            </div>
            
            <p className="hero-subtitle">
              Erstellen Sie hochwertige Trainingsdaten für Computer Vision Modelle
              <br />
              <span className="subtitle-highlight">vollständig lokal</span>, 
              <span className="subtitle-highlight"> DSGVO-konform</span> und 
              <span className="subtitle-highlight"> ohne manuelle Annotation</span>.
            </p>
            
            <div className="hero-cta-group">
              <button
                onClick={() => setShowAuth(true)}
                className="hero-cta-primary-minimal"
                aria-label="Jetzt starten und registrieren"
              >
                <span>Jetzt starten</span>
                <span className="cta-arrow" aria-hidden="true">→</span>
                <div className="cta-ripple" aria-hidden="true"></div>
              </button>
              <a
                href="#features"
                className="hero-cta-secondary-minimal"
                aria-label="Mehr über Features erfahren"
              >
                <span>Mehr erfahren</span>
                <span className="cta-underline" aria-hidden="true"></span>
              </a>
            </div>
            
            {/* Stats */}
            <div className="hero-stats">
              <div className="hero-stat">
                <div className="stat-value">100%</div>
                <div className="stat-label">Lokal</div>
              </div>
              <div className="hero-stat-divider"></div>
              <div className="hero-stat">
                <div className="stat-value">DSGVO</div>
                <div className="stat-label">Konform</div>
              </div>
              <div className="hero-stat-divider"></div>
              <div className="hero-stat">
                <div className="stat-value">0</div>
                <div className="stat-label">Setup-Zeit</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="features-section" aria-label="Features">
        <div className="features-container">
          <header className="features-header">
            <h2>Features</h2>
            <p>Alles was Sie brauchen, um professionelle Trainingsdaten zu generieren</p>
          </header>

          {/* Current Features */}
          <div className="features-grid" role="list">
            {currentFeatures.map((feature, index) => (
              <article key={index} className="feature-card" role="listitem">
                <div className="feature-icon" aria-hidden="true">
                  {feature.icon}
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </article>
            ))}
          </div>

          {/* Coming Soon Features */}
          <div className="coming-soon-section">
            <header className="coming-soon-header">
              <h3>Coming Soon</h3>
              <span className="coming-soon-badge" aria-label="In Entwicklung">In Entwicklung</span>
            </header>
            <div className="coming-soon-grid" role="list">
              {comingSoonFeatures.map((feature, index) => (
                <article key={index} className="coming-soon-card" role="listitem">
                  <div className="feature-icon" aria-hidden="true">
                    {feature.icon}
                  </div>
                  <h3>{feature.title}</h3>
                  <p>{feature.description}</p>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section" aria-label="Call to Action">
        <div className="cta-background-pattern"></div>
        <div className="cta-container">
          <div className="cta-content-wrapper">
            <div className="cta-badge">Kostenlos starten</div>
            <h2 className="cta-title">Bereit, Ihre Trainingsdaten zu generieren?</h2>
            <p className="cta-description">
              Starten Sie jetzt und erstellen Sie professionelle Datensätze für Ihre ML-Modelle - 
              vollständig lokal und DSGVO-konform.
            </p>
            <div className="cta-features">
              <div className="cta-feature-item">
                <span className="cta-feature-icon">✓</span>
                <span>Keine Kreditkarte erforderlich</span>
              </div>
              <div className="cta-feature-item">
                <span className="cta-feature-icon">✓</span>
                <span>Sofort loslegen</span>
              </div>
              <div className="cta-feature-item">
                <span className="cta-feature-icon">✓</span>
                <span>100% kostenlos</span>
              </div>
            </div>
            <button
              onClick={() => setShowAuth(true)}
              className="cta-button"
              aria-label="Kostenlos registrieren"
            >
              <span>Jetzt kostenlos starten</span>
              <span className="cta-arrow" aria-hidden="true">→</span>
              <div className="cta-button-shine"></div>
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer" role="contentinfo">
        <div className="footer-container">
          <div className="footer-top">
            <div className="footer-brand">
              <SynioLogo height={36} aria-label="SYNIO Logo" />
              <p className="footer-tagline">
                Synthetic Data Generator für Machine Learning und Computer Vision. 
                Erstellen Sie hochwertige Trainingsdaten vollständig lokal, 
                DSGVO-konform und ohne manuelle Annotation.
              </p>
            </div>
            <div className="footer-info">
              <div className="footer-info-item">
                <FaLock className="footer-info-icon" aria-hidden="true" />
                <div>
                  <div className="footer-info-label">DSGVO-konform</div>
                  <div className="footer-info-desc">100% datenschutzkonform</div>
                </div>
              </div>
              <div className="footer-info-item">
                <FaDatabase className="footer-info-icon" aria-hidden="true" />
                <div>
                  <div className="footer-info-label">100% Lokal</div>
                  <div className="footer-info-desc">Keine Cloud-Uploads</div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="footer-divider"></div>
          
          <div className="footer-bottom">
            <div className="footer-copyright">
              <span>© 2024 SYNIO</span>
              <span className="footer-separator">•</span>
              <span>Alle Rechte vorbehalten</span>
            </div>
            <div className="footer-legal">
              <button
                onClick={() => setShowImpressum(true)}
                className="footer-legal-link"
                aria-label="Impressum anzeigen"
              >
                Impressum
              </button>
            </div>
          </div>
        </div>
      </footer>
    </main>
  );
};

export default LandingPage;
